import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AgeCategory, AvatarPlaceholder } from './Live.style';
import { useMatchPolling } from '../../hooks/useMatchPolling';
import {
  selectBestPlayersByTeam,
  selectCurrentRace,
  selectCurrentRaceIndex,
  selectIsMatchFinished,
  selectMatch,
  selectPastRaces,
  selectRaces,
  selectUpcomingRaces,
} from 'app/slice/selectors';
import { formatTs } from 'utils/utils';
import Loader from 'components/Loader/Loader.style';
import { BLACK } from 'styles/colors';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import RacePlayers from 'components/RacePlayers';
import RaceResult from 'components/RaceResult';
import TeamSummary from 'components/TeamSummary';
import MatchNotFound from 'components/MatchNotFound';
import { Helmet } from 'react-helmet-async';
import { LeagueName } from 'app/consts';
import { useLeagueWS } from 'app/hooks/useLeagueWS';
import { getColSizes } from './utils';
import { useGetLeagueQuery } from 'app/api';
import SvgIcon from 'components/SvgIcon';
import UserIcon from '../../../assets/Icon/user.svg';
import { remToPx } from 'utils/utils';

const Live: React.FC = () => {
  const { leagueId = '', matchId = '' } = useParams();
  const match = useSelector(selectMatch);
  const isMatchFinished = useSelector(selectIsMatchFinished);
  const races = useSelector(selectRaces);
  const pastRaces = useSelector(selectPastRaces) || [];
  const currentRace = useSelector(selectCurrentRace);
  const currentRaceIndex = useSelector(selectCurrentRaceIndex);
  const upcomingRaces = useSelector(selectUpcomingRaces);

  const { data: leagueData, isLoading: leagueLoading } = useGetLeagueQuery(
    { league_id: leagueId },
    { skip: !leagueId },
  );

  const bestPlayersByTeam = useSelector(selectBestPlayersByTeam);
  const teamsWithPlayers = Object.entries(bestPlayersByTeam).filter(
    ([, players]) => players.length > 0,
  );
  const { xs, md } = getColSizes(teamsWithPlayers.length);

  const { loading, error, refetchMatch, refetchRuns } = useMatchPolling(
    leagueId,
    matchId,
  );
  const initialLoadError = error && !match;

  const ageCategory = leagueData?.attrs?.ageCategory;

  useLeagueWS({
    leagueId,
    matchId,
    match,
    refetchMatch,
    refetchRuns,
  });

  if (leagueLoading || loading)
    return (
      <div className="w-100 d-flex justify-content-center align-items-center">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '10rem' }}
        >
          <Loader color={BLACK} size={10} />
        </div>
      </div>
    );
  if (initialLoadError)
    return (
      <div className="w-100 h-100 d-flex justify-content-center align-items-center">
        <MatchNotFound />
      </div>
    );

  return (
    <>
      <Helmet>
        <title>
          Mecz {ageCategory} - {match?.name}
        </title>
        <meta
          name="description"
          content={`Mecz ${match?.teams[0].name} vs ${match?.teams[1].name} w ramach ${LeagueName}`}
        />
        <meta
          property="og:title"
          content={`Mecz ${ageCategory} - ${match?.name}`}
        />
        <meta
          property="og:description"
          content={`Mecz ${match?.teams[0].name} vs ${match?.teams[1].name} w ramach ${LeagueName}`}
        />
        <meta
          name="twitter:title"
          content={`Mecz ${ageCategory} - ${match?.name}`}
        />
        <meta
          name="twitter:description"
          content={`Mecz ${match?.teams[0].name} vs ${match?.teams[1].name} w ramach ${LeagueName}`}
        />
      </Helmet>
      <div className="w-100">
        <section className="container pt-5 text-body-secondary">
          <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ columnGap: '0.5rem', rowGap: '0.5rem' }}
          >
            <div
              className="d-flex flex-row align-items-center justify-content-center"
              style={{ columnGap: '1rem', rowGap: '0.5rem' }}
            >
              <AgeCategory>{ageCategory}</AgeCategory>
              <h1 className="mb-0">{match?.name}</h1>
            </div>
            <div
              className="d-flex flex-row align-items-center justify-content-center"
              style={{ columnGap: '0.5rem', rowGap: '0.5rem' }}
            >
              <span>{formatTs((match?.start_date || 0) * 1000)}</span>
            </div>
          </div>
        </section>
        <section>
          <Container className="my-4 justify-content-center d-flex">
            <TeamSummary teams={match?.teams!} theme="light" size={1} />
          </Container>
        </section>
        <section>
          <Container>
            {pastRaces.length > 0 && isMatchFinished && (
              <Row xs={1} className="justify-content-center">
                <Col className="col-12 col-lg-8">
                    <Card className="mb-4">
                      <div className="p-3 p-md-4">
                        <h2 className="h4">Najlepsi zawodnicy</h2>
                        <Row className="g-4">
                          {teamsWithPlayers.map(([teamId, players]) => {
                            const team = match?.teams.find(
                              (team) => team.id === teamId,
                            );
                            return (
                              <Col key={teamId} xs={xs} md={md}>
                                <div className="d-flex flex-column">
                                  <h3
                                    className="h5"
                                    style={{
                                      color: team?.attrs.colors?.primary,
                                    }}
                                  >
                                    {team?.name}
                                  </h3>
                                  {players.map((p) => (
                                    <div
                                      key={p.nickname}
                                      className="d-flex flex-row align-items-center justify-content-between border-bottom ps-2 pe-2 pe-md-3 position-relative overflow-hidden oswald-regular"
                                      style={{
                                        borderLeft: `0.5rem solid ${p.extra?.colors?.primary}`,
                                        columnGap: `0.5rem`
                                      }}
                                    >
                                      {/* Photo + name */}
                                      <div
                                        className="text-uppercase d-flex flex-row align-items-center pt-2 pb-2 oswald-regular lh-1"
                                        style={{
                                          columnGap: `0.5rem`,
                                          fontSize: `1.25rem`,
                                        }}
                                      >
                                        {/* Photo */}
                                        <div
                                          className="d-flex justify-content-center align-items-center rounded"
                                          style={{
                                              backgroundColor: p.extra?.colors?.primary,
                                              width: remToPx(3),
                                              height: remToPx(3)
                                            }}
                                          >
                                          {p.avatar ? (
                                            <img
                                              src={p.avatar}
                                              width={remToPx(3)}
                                              height={remToPx(3)}
                                              className="rounded"
                                            />
                                          ) : (
                                            <AvatarPlaceholder
                                              className="d-flex justify-content-center align-items-center rounded"
                                              style={{
                                                backgroundColor:
                                                  p.extra?.colors?.primary,
                                              }}
                                            >
                                              <SvgIcon
                                                src={UserIcon}
                                                width="2rem"
                                                height="2rem"
                                              />
                                            </AvatarPlaceholder>
                                          )}

                                        </div>
                                        {/* Name */}
                                        <div>{p.name}</div>
                                      </div>
                                      {/* Result */}
                                      <div className="text-end">
                                        {p.points} pkt
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      </div>
                    </Card>
                </Col>
              </Row>
            )}
            <Row xs={1} xl={2} className="justify-content-center">
              {pastRaces.length > 0 && (
                <Col className="col-12 col-lg-8">
                  <Card className="mb-4">
                    <div className="p-3 p-md-4">
                      <h2 className="h4">Wyniki wyścigów</h2>
                      <div
                        className="d-flex flex-column"
                        style={{ gap: `1rem` }}
                      >
                        {pastRaces?.map((race) => (
                          <RaceResult
                            race={race}
                            key={race.id}
                            raceIndex={
                              races?.findIndex((r) => r.id === race.id) || 0
                            }
                          />
                        ))}
                      </div>
                    </div>
                  </Card>

                </Col>
              )}
              {(currentRace || (upcomingRaces && upcomingRaces.length > 0)) && (
                <Col>
                  <Card className="bg-secondary">
                    <div className="p-3 p-md-4 pb-0 pb-md-0">
                      <h2 className="h4">Plan wyścigów</h2>
                    </div>
                    {currentRace && (
                      <div className="p-3 p-md-4 bg-gradient-primary">
                        {/* <h3 className="h5 text-light">Teraz</h3> */}
                        <RacePlayers
                          race={currentRace}
                          raceIndex={currentRaceIndex}
                          size={1}
                        />
                      </div>
                    )}
                    {upcomingRaces && upcomingRaces.length > 0 && (
                      <>
                        <div className="p-3 p-md-4">
                          <div
                            className="d-flex flex-column"
                            style={{ gap: `2rem` }}
                          >
                            {upcomingRaces?.map((race) => (
                              <RacePlayers
                                race={race}
                                key={race.id}
                                raceIndex={
                                  races?.findIndex((r) => r.id === race.id) || 0
                                }
                                size={1}
                              />
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </Card>
                </Col>
              )}
            </Row>
          </Container>
        </section>
      </div>
    </>
  );
};

export default Live;
